import { useCallback, useEffect, useMemo, useRef } from "react";


const FEEDBACK_PROFILES = {
    success: {
        oscillatorType: "sine",
        frequencies: [ 660, 880 ],
        duration: 0.22,
        volume: 0.12
    },
    error: {
        oscillatorType: "triangle",
        frequencies: [ 440, 330 ],
        duration: 0.28,
        volume: 0.1
    }
};

const ATTACK_DURATION = 0.012;
const RELEASE_DURATION = 0.035;

const getAudioContextConstructor = () => {
    if (typeof window === "undefined") {
        return null;
    }

    return window.AudioContext || window.webkitAudioContext || null;
};

const safeDisconnect = (node) => {
    try {
        node?.disconnect?.();
    } catch (e) {
        // An audio node can already be disconnected by its onended callback.
    }
};

const useAttendanceFeedbackAudio = () => {
    const audioContextRef = useRef(null);
    const activeVoicesRef = useRef(new Set());
    const playbackVersionRef = useRef(0);
    const mountedRef = useRef(true);

    const releaseVoice = useCallback((voice) => {
        if (!voice || voice.released) {
            return;
        }

        voice.released = true;
        voice.oscillator.onended = null;
        activeVoicesRef.current.delete(voice);
        safeDisconnect(voice.oscillator);
        safeDisconnect(voice.gainNode);
    }, []);

    const cancelActiveVoices = useCallback(() => {
        activeVoicesRef.current.forEach((voice) => {
            try {
                voice.oscillator.stop();
            } catch (e) {
                // A scheduled oscillator may already have stopped naturally.
            }

            releaseVoice(voice);
        });
        activeVoicesRef.current.clear();
    }, [ releaseVoice ]);

    const getAudioContext = useCallback(() => {
        const currentContext = audioContextRef.current;

        if (currentContext && currentContext.state !== "closed") {
            return currentContext;
        }

        const AudioContextConstructor = getAudioContextConstructor();

        if (!AudioContextConstructor) {
            return null;
        }

        try {
            const nextContext = new AudioContextConstructor();

            audioContextRef.current = nextContext;

            return nextContext;
        } catch (e) {
            return null;
        }
    }, []);

    const resumeAudioContext = useCallback(async (audioContext) => {
        if (!audioContext || audioContext.state === "closed") {
            return false;
        }

        if (audioContext.state === "running") {
            return true;
        }

        if (!audioContext.resume) {
            return false;
        }

        try {
            await Promise.resolve(audioContext.resume());

            return audioContext.state === "running";
        } catch (e) {
            return false;
        }
    }, []);

    const unlockAudio = useCallback(async () => {
        if (!mountedRef.current) {
            return;
        }

        const audioContext = getAudioContext();

        await resumeAudioContext(audioContext);
    }, [
        getAudioContext,
        resumeAudioContext
    ]);

    const stopFeedback = useCallback(() => {
        playbackVersionRef.current += 1;
        cancelActiveVoices();
    }, [ cancelActiveVoices ]);

    const playFeedback = useCallback(async (feedbackType) => {
        const profile = FEEDBACK_PROFILES[feedbackType];

        if (!profile || !mountedRef.current) {
            return;
        }

        const playbackVersion = playbackVersionRef.current + 1;

        playbackVersionRef.current = playbackVersion;
        cancelActiveVoices();

        const audioContext = getAudioContext();
        const ready = await resumeAudioContext(audioContext);

        if (
            !ready ||
            !mountedRef.current ||
            playbackVersionRef.current !== playbackVersion ||
            audioContextRef.current !== audioContext
        ) {
            return;
        }

        const toneDuration = profile.duration / profile.frequencies.length;
        const startTime = audioContext.currentTime;

        try {
            profile.frequencies.forEach((frequency, index) => {
                const toneStart = startTime + (toneDuration * index);
                const toneEnd = toneStart + toneDuration;
                const attackEnd = toneStart + Math.min(ATTACK_DURATION, toneDuration / 3);
                const releaseStart = toneEnd - Math.min(RELEASE_DURATION, toneDuration / 3);
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                const voice = {
                    oscillator,
                    gainNode,
                    released: false
                };

                oscillator.type = profile.oscillatorType;
                oscillator.frequency.setValueAtTime(frequency, toneStart);
                gainNode.gain.setValueAtTime(0, toneStart);
                gainNode.gain.linearRampToValueAtTime(profile.volume, attackEnd);
                gainNode.gain.setValueAtTime(profile.volume, releaseStart);
                gainNode.gain.linearRampToValueAtTime(0, toneEnd);

                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                oscillator.onended = () => releaseVoice(voice);
                activeVoicesRef.current.add(voice);
                oscillator.start(toneStart);
                oscillator.stop(toneEnd);
            });
        } catch (e) {
            cancelActiveVoices();
        }
    }, [
        cancelActiveVoices,
        getAudioContext,
        releaseVoice,
        resumeAudioContext
    ]);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            playbackVersionRef.current += 1;
            cancelActiveVoices();

            const audioContext = audioContextRef.current;

            audioContextRef.current = null;

            if (!audioContext || audioContext.state === "closed" || !audioContext.close) {
                return;
            }

            try {
                Promise.resolve(audioContext.close()).catch(() => undefined);
            } catch (e) {
                // Audio shutdown must never affect Attendance unmounting.
            }
        };
    }, [ cancelActiveVoices ]);

    return useMemo(() => ({
        unlockAudio,
        playFeedback,
        stopFeedback
    }), [
        playFeedback,
        stopFeedback,
        unlockAudio
    ]);
};

export default useAttendanceFeedbackAudio;
